import React, { useMemo } from "react";
import Select, { components, GroupBase, StylesConfig } from "react-select";
import { useI18n } from "../i18n";

interface Airport {
  code: string;
  name: string;
  country: string;
}

interface AirportOption {
  value: string;
  label: string;
  country: string;
  originalCountry: string;
  flag: string;
  city: string;
  code: string;
}

interface AirportSelectorProps {
  airports: Airport[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  isSearchable?: boolean;
  className?: string;
  isDisabled?: boolean;
}

// Country code to flag emoji mapping
const getCountryFlag = (countryName: string): string => {
  const countryToCode: { [key: string]: string } = {
    Italy: "IT",
    Spain: "ES",
    France: "FR",
    Germany: "DE",
    "United Kingdom": "GB",
    Ireland: "IE",
    Portugal: "PT",
    Greece: "GR",
    Poland: "PL",
    Netherlands: "NL",
    Belgium: "BE",
    Austria: "AT",
    "Czech Republic": "CZ",
    Hungary: "HU",
    Croatia: "HR",
    Romania: "RO",
    Bulgaria: "BG",
    Sweden: "SE",
    Denmark: "DK",
    Norway: "NO",
    Finland: "FI",
    Switzerland: "CH",
    Morocco: "MA",
    Tunisia: "TN",
    Egypt: "EG",
    Jordan: "JO",
    Israel: "IL",
    Turkey: "TR",
    Malta: "MT",
    Cyprus: "CY",
    Albania: "AL",
    Serbia: "RS",
    "Bosnia & Herzegovina": "BA",
    Montenegro: "ME",
    "North Macedonia": "MK",
    Latvia: "LV",
    Lithuania: "LT",
    Estonia: "EE",
    Slovakia: "SK",
    Slovenia: "SI",
    Luxembourg: "LU",
  };

  const code = countryToCode[countryName];
  if (!code) return "🌍";

  // Convert country code to flag emoji
  const codePoints = code
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Get localized country name
const getLocalizedCountry = (countryName: string, t: (key: string) => string): string => {
  return t(`countries.${countryName}`) || countryName;
};

// Get localized city name
const getLocalizedCity = (cityName: string, t: (key: string) => string): string => {
  // First try the full city name
  const fullTranslation = t(`cities.${cityName}`);
  if (fullTranslation !== `cities.${cityName}`) {
    return fullTranslation;
  }
  
  // If not found, try to translate individual words (for compound names like "Milan Bergamo")
  const words = cityName.split(' ');
  const translatedWords = words.map(word => {
    const wordTranslation = t(`cities.${word}`);
    return wordTranslation !== `cities.${word}` ? wordTranslation : word;
  });
  
  // If any word was translated, use the translated version
  if (translatedWords.some((word, index) => word !== words[index])) {
    return translatedWords.join(' ');
  }
  
  // Fallback to original name
  return cityName;
};

// Custom option component with flag and styling
const CustomOption = (props: any) => {
  const { data, innerRef, innerProps } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        padding: "10px 12px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        backgroundColor: props.isFocused ? "#f0f8ff" : "white",
        borderLeft: props.isFocused ? "3px solid #0d6efd" : "3px solid transparent",
        transition: "all 0.2s ease",
      }}
    >
      <span style={{ fontSize: "20px", marginRight: "10px" }}>{data.flag}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 500, color: "#212529", fontSize: "14px" }}>
          <span
            style={{
              display: "inline-block",
              backgroundColor: "#0d6efd",
              color: "white",
              padding: "2px 6px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: 600,
              marginRight: "8px",
            }}
          >
            {data.code}
          </span>
          {data.city}
        </div>
        <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "2px" }}>
          {data.country}
        </div>
      </div>
    </div>
  );
};

// Custom single value component
const CustomSingleValue = (props: any) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <span style={{ fontSize: "18px", marginRight: "8px" }}>{data.flag}</span>
        <span style={{ fontWeight: 600, marginRight: "6px" }}>{data.code}</span>
        <span>- {data.city}</span>
      </div>
    </components.SingleValue>
  );
};

// Custom group heading
const CustomGroupHeading = (props: any) => {
  return (
    <div
      style={{
        backgroundColor: "#e9ecef",
        padding: "8px 12px",
        fontWeight: 600,
        fontSize: "13px",
        color: "#495057",
        position: "sticky",
        top: 0,
        zIndex: 1,
        borderBottom: "1px solid #dee2e6",
      }}
    >
      {props.children}
    </div>
  );
};

const AirportSelector: React.FC<AirportSelectorProps> = ({
  airports,
  value,
  onChange,
  placeholder = "Select an airport...",
  isSearchable = true,
  className = "",
  isDisabled = false,
}) => {
  const { t } = useI18n();
  // Transform and group airports
  const { options, groupedOptions } = useMemo(() => {
    const airportOptions: AirportOption[] = airports.map((airport) => ({
      value: airport.code,
      label: `${airport.code} - ${getLocalizedCity(airport.name, t)}, ${getLocalizedCountry(airport.country, t)}`,
      country: getLocalizedCountry(airport.country, t),
      originalCountry: airport.country,
      flag: getCountryFlag(airport.country),
      city: getLocalizedCity(airport.name, t),
      code: airport.code,
    }));

    // Group by country
    const grouped: { [key: string]: AirportOption[] } = {};
    airportOptions.forEach((option) => {
      if (!grouped[option.country]) {
        grouped[option.country] = [];
      }
      grouped[option.country].push(option);
    });

    // Convert to react-select group format
    const groupedForSelect: GroupBase<AirportOption>[] = Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([country, opts]) => ({
        label: `${getCountryFlag(opts[0].originalCountry)} ${country}`,
        options: opts.sort((a, b) => a.city.localeCompare(b.city)),
      }));

    return {
      options: airportOptions,
      groupedOptions: groupedForSelect,
    };
  }, [airports, t]);

  const selectedOption = value ? options.find((opt) => opt.value === value) : null;

  // Custom styles for modern look
  const customStyles: StylesConfig<AirportOption, false> = {
    control: (base, state) => ({
      ...base,
      minHeight: "48px",
      borderRadius: "8px",
      border: state.isFocused ? "2px solid #0d6efd" : "2px solid #dee2e6",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(13, 110, 253, 0.15)" : "none",
      "&:hover": {
        borderColor: "#0d6efd",
      },
      transition: "all 0.2s ease",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#6c757d",
      fontSize: "14px",
    }),
    input: (base) => ({
      ...base,
      fontSize: "14px",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      overflow: "hidden",
      marginTop: "4px",
      border: "1px solid #dee2e6",
    }),
    menuList: (base) => ({
      ...base,
      padding: 0,
      maxHeight: "300px",
    }),
    option: () => ({
      // Custom option styling is in CustomOption component
    }),
    groupHeading: () => ({
      // Custom group heading styling is in CustomGroupHeading component
    }),
  };

  return (
    <Select
      className={className}
      options={groupedOptions}
      value={selectedOption}
      onChange={(option) => onChange(option?.value || "")}
      placeholder={placeholder}
      isSearchable={isSearchable}
      isDisabled={isDisabled}
      styles={customStyles}
      components={{
        Option: CustomOption,
        SingleValue: CustomSingleValue,
        GroupHeading: CustomGroupHeading,
      }}
      filterOption={(option, inputValue) => {
        const searchText = inputValue.toLowerCase();
        return (
          option.data.code.toLowerCase().includes(searchText) ||
          option.data.city.toLowerCase().includes(searchText) ||
          option.data.country.toLowerCase().includes(searchText)
        );
      }}
      noOptionsMessage={() => "No airports found"}
    />
  );
};

export default AirportSelector;
